import axios from "../utils/axios.instance";
import api from "../api";
import handleError from "./handle-error";

export const getAccountDetails = async () => {
  try {
    const { data } = await axios.get(api.getAccountDetails);
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const getStripeAccountDetails = async (accountId) => {
  try {
    const { data } = await axios.get(
      `${api.getStripeAccountDetails}/${accountId}`,
    );
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const createStripeAccountSession = async (accountId) => {
  try {
    const { data } = await axios.post(api.createStripeAccountSession, {
      accountId,
    });
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const createCheckoutSession = async (priceId) => {
  try {
    const { data } = await axios.post(api.createCheckoutSession, { priceId });
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const getReferrals = async () => {
  try {
    const { data } = await axios.get(api.getReferrals);
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const createCardPaymentIntent = async (
  accountId,
  amount,
  paymentMethodId,
  recipientId,
  candidateId,
  jobId,
  jobApplicationId,
) => {
  try {
    const { data } = await axios.post(api.createCardPaymentIntent, {
      accountId,
      amount,
      paymentMethodId,
      recipientId,
      candidateId,
      jobId,
      jobApplicationId,
    });
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const createSetupIntent = async () => {
  try {
    const { data } = await axios.post(api.createSetupIntent);
    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const createBankPaymentIntent = async (accountId, amount) => {
  try {
    const { data } = await axios.post(api.createBankPaymentIntent, {
      accountId,
      amount,
    });
    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const addCardPaymentMethod = async (token) => {
  try {
    const { data } = await axios.post(api.addCardPaymentMethod, {
      token,
    });
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const addBankDetails = async (token) => {
  try {
    const { data } = await axios.post(api.addBankDetails, {
      token,
    });
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const getBanksLists = async () => {
  try {
    const { data } = await axios.get(api.getBankLists);
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const getBankDetails = async (bankId) => {
  try {
    const { data } = await axios.get(`${api.fetchBankDetails}/${bankId}`);
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const updateBankDetails = async (bankId, name) => {
  try {
    const { data } = await axios.post(api.updateBankDetails, {
      bankId,
      name,
    });
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const makeBankDefault = async (bankId) => {
  try {
    const { data } = await axios.post(api.makeDefaultBank, {
      bankId,
    });
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const deleteBank = async (bankId) => {
  try {
    const { data } = await axios.delete(`${api.deleteBank}/${bankId}`);
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const getBalanceInfo = async () => {
  try {
    const { data } = await axios.get(api.getBalanceDetails);
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const withDrawBalance = async (amount) => {
  try {
    const { data } = await axios.post(api.withDrawBalance, {
      amount,
    });
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const getPaymentMethods = async (type) => {
  try {
    const { data } = await axios.get(`${api.fetchPaymentMethods}/${type}`);
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const addBankAccountForCustomer = async (paymentMethodId) => {
  try {
    const { data } = await axios.post(api.addCustomerBankAccount, {
      paymentMethodId,
    });
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const deletePaymentMethod = async (paymentMethodId) => {
  try {
    const { data } = await axios.delete(
      `${api.deletePaymentMethod}/${paymentMethodId}`,
    );
    return data;
  } catch (err) {
    return handleError(err);
  }
};

export const setDefaultPaymentMethod = async (paymentMethodId) => {
  try {
    const { data } = await axios.post(api.setDefaultPaymentMethod, {
      paymentMethodId,
    });
    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getCheckoutDetails = async (payload) => {
  try {
    const { data } = await axios.post(api.getCheckoutDetails, {
      ...payload,
    });
    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const createPaymentHistory = async (payload) => {
  try {
    const { data } = await axios.post(api.createPaymentHistory, {
      ...payload,
    });
    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getPaymentHistories = async (
  page,
  type,
  searchText,
  startDate,
  endDate,
) => {
  try {
    const { data } = await axios.get(api.getPaymentHistories, {
      params: { page, type, searchText, startDate, endDate },
    });
    return data;
  } catch (error) {
    return handleError(error);
  }
};

interface getReferralPaymentHistoriesParams {
  limit?: number;
  page?: number;
  searchText?: string;
}

export const getReferralPaymentHistories = async ({
  page = 1,
  limit = 100,
  searchText,
}: getReferralPaymentHistoriesParams) => {
  try {
    const params = new URLSearchParams();
    if (limit) params.append("limit", limit.toString());
    if (page) params.append("page", page.toString());
    if (searchText) params.append("searchText", searchText.toString());
    const generalURL = api.getReferralPaymentHistories;
    const URL = params.toString()
      ? generalURL + `?${params?.toString()}`
      : generalURL;
    const { data } = await axios.get(URL);
    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const createReferralTransaction = async (payload) => {
  try {
    const { data } = await axios.post(api.createReferralTransaction, {
      ...payload,
    });
    return data;
  } catch (error) {
    return handleError(error);
  }
};

interface GetPaymentHistoryDetailsParams {
  id: string; //payment history id
  type: "jobpost" | "referralpayment" | "subscription";
  by?: "job" | "payment";
}

export const getPaymentHistoryDetails = async ({
  id,
  type,
  by = "payment",
}: GetPaymentHistoryDetailsParams) => {
  try {
    const params = new URLSearchParams();
    if (by) params.append("by", by);
    const { data } = await axios.get(
      `${api.getPaymentHistoryDetails}/${id}/${type}?${params?.toString()}`,
    );
    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getReceiptUserDetails = async (userId, jobId) => {
  try {
    const { data } = await axios.get(
      `${api.receiptUserDetails}/${userId}/${jobId}`,
    );
    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getJobPostingPriceDetails = async () => {
  try {
    const { data } = await axios.get(api.getJobPostingPriceDetails);
    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const createPaymentIntent = async (
  amount,
  currency,
  paymentMethodId,
  jobId,
) => {
  try {
    const { data } = await axios.post(api.createPaymentIntent, {
      amount,
      currency,
      paymentMethodId,
      jobId,
    });
    return data;
  } catch (error) {
    return handleError(error);
  }
};
